export interface FlagPattern {
  pattern: string; // regex-compatible string to match in JD
  label: string; // display name
  meaning: string; // what it means
  advice: string; // what to ask or do about it
}

export const redFlags: FlagPattern[] = [
  {
    pattern: 'rockstar',
    label: 'Rockstar',
    meaning:
      'This buzzword often signals a company that expects one person to do the work of many. They want exceptional output without providing exceptional support or compensation.',
    advice:
      'Ask about team size, individual responsibilities, and what a typical workweek looks like. If the role covers multiple job functions, it should come with a matching salary.',
  },
  {
    pattern: 'ninja',
    label: 'Ninja',
    meaning:
      'Similar to "rockstar," this term suggests the company values flashy hustle culture over structured processes and sustainable work practices.',
    advice:
      'Dig into their development process, documentation practices, and whether there are clear role boundaries. A well-run team doesn\'t need "ninjas."',
  },
  {
    pattern: 'guru',
    label: 'Guru',
    meaning:
      'Companies using this term may expect you to be the sole expert with no support, mentorship, or room for growth. It can also signal a lack of seriousness about the role.',
    advice:
      'Ask who else is on the team with similar expertise. Find out if they have existing documentation, processes, or if you\'d be building everything from scratch alone.',
  },
  {
    pattern: 'wear(?:s|ing)?\\s+many\\s+hats',
    label: 'Wear Many Hats',
    meaning:
      'This usually means the company is understaffed and you\'ll be expected to cover multiple roles. You might be hired as a designer but also end up doing project management and customer support.',
    advice:
      'Ask for a detailed list of all responsibilities. Clarify which tasks are primary vs. secondary. Ask if there are plans to hire additional team members.',
  },
  {
    pattern: 'work\\s+hard\\s*,?\\s*play\\s+hard',
    label: 'Work Hard, Play Hard',
    meaning:
      'This phrase almost always translates to long hours with the occasional pizza party or happy hour to compensate. The "play" rarely offsets the "work."',
    advice:
      'Ask about average working hours, weekend expectations, and overtime policies. Request to speak with current employees about work-life balance.',
  },
  {
    pattern: 'like\\s+a\\s+family',
    label: 'Like a Family',
    meaning:
      'While it sounds warm, "family" culture often means blurred boundaries, guilt-tripping about work hours, difficulty saying no, and an expectation that the job comes before your personal life.',
    advice:
      'Ask about boundaries between work and personal time. Inquire about turnover rates. Ask what happens when someone needs to leave on time for personal commitments.',
  },
  {
    pattern: 'unlimited\\s+(?:PTO|paid\\s+time\\s+off|vacation)',
    label: 'Unlimited PTO (as only benefit)',
    meaning:
      'Unlimited PTO with no other benefits mentioned often means nobody actually takes time off because there\'s no formal tracking, and there\'s social pressure to keep working. Companies also save money by not paying out unused vacation days when you leave.',
    advice:
      'Ask what the average number of vacation days actually taken by employees is. Ask if there\'s a minimum PTO policy. Check if departing employees get any PTO payout.',
  },
  {
    pattern: '(?:fast[- ]paced|high[- ]pressure).*(?:fast[- ]paced|high[- ]pressure)',
    label: 'Fast-Paced + High Pressure',
    meaning:
      'When a job description emphasizes both speed and pressure, it signals a chaotic environment with constant firefighting, unrealistic deadlines, and likely burnout.',
    advice:
      'Ask about their project planning process, how deadlines are set, and what support systems exist when things get stressful. Ask about employee retention rates.',
  },
  {
    pattern: 'must\\s+be\\s+willing\\s+to\\s+travel\\s+extensively',
    label: 'Must Travel Extensively',
    meaning:
      'Extensive travel without clear parameters could mean 50-80% of your time on the road. This is a lifestyle decision that should be fully transparent in the listing.',
    advice:
      'Ask for a specific travel percentage. Find out who covers travel expenses and reimbursement timelines. Ask if the travel requirement changes seasonally.',
  },
  {
    pattern: 'on[- ]call\\s+24\\/7',
    label: 'On-Call 24/7',
    meaning:
      'Being expected to be available around the clock suggests poor infrastructure planning, insufficient staffing, or a culture where personal time is not respected.',
    advice:
      'Ask about on-call rotation schedules, compensation for after-hours work, and how frequently on-call engineers are actually paged. Ask about incident frequency.',
  },
  {
    pattern: 'salary\\s+(?:DOE|depends\\s+on\\s+experience|commensurate\\s+with\\s+experience)',
    label: 'Salary DOE / Depends on Experience',
    meaning:
      'When a company refuses to share a salary range, they often plan to pay as little as possible. This phrase gives them leverage to lowball candidates and creates pay inequity.',
    advice:
      'Research the market rate for this role on Glassdoor, Levels.fyi, or Payscale. State your expected range early in the process. If they refuse to share a range at all, consider it a negotiation red flag.',
  },
  {
    pattern: 'unpaid\\s+(?:trial|test|internship|probation)',
    label: 'Unpaid Trial Period',
    meaning:
      'Asking candidates to work for free is exploitative and often illegal. Legitimate companies pay for all work performed, including trial periods and take-home projects.',
    advice:
      'Decline unpaid work. A reasonable skills assessment should take 2-4 hours maximum. If they insist on a longer trial, it should be compensated at your market rate.',
  },
  {
    pattern: 'equity[- ]only|equity\\s+in\\s+lieu\\s+of\\s+salary',
    label: 'Equity Only (No Salary)',
    meaning:
      'Offering equity without a salary means the company either cannot afford to pay you or does not value your time. Equity in an early-stage startup is essentially a lottery ticket.',
    advice:
      'Unless you have significant savings and genuinely believe in the company, avoid equity-only roles. If you consider it, get the equity terms in writing and have a lawyer review them.',
  },
  {
    pattern: '(?:recent\\s+grad|entry[- ]level|junior).*(?:\\d{2,}\\+?\\s+years|[5-9]\\+?\\s+years)',
    label: 'Entry-Level + Excessive Experience Required',
    meaning:
      'Asking for years of experience in an entry-level role is contradictory. This usually means the company wants senior talent at junior pay, or the hiring manager does not understand the role.',
    advice:
      'If the responsibilities genuinely match your skills, apply anyway but negotiate for appropriate compensation. Many candidates self-select out of these listings, reducing competition.',
  },
  {
    pattern: 'hit\\s+the\\s+ground\\s+running',
    label: 'Hit the Ground Running',
    meaning:
      'This signals little to no onboarding, training, or ramp-up time. You\'ll be expected to perform at full capacity from day one, which is unrealistic for any new role.',
    advice:
      'Ask about their onboarding process, whether there\'s documentation for existing systems, and how long previous hires took to ramp up. A company that invests in onboarding invests in retention.',
  },
  {
    pattern: 'no\\s+(?:remote|work\\s+from\\s+home).*(?:non[- ]?negotiable|mandatory|required)',
    label: 'No Remote (Non-Negotiable)',
    meaning:
      'Rigidly mandating in-office work with no flexibility can indicate a management style based on surveillance rather than trust and results.',
    advice:
      'If in-office is acceptable to you, ask why the policy exists and whether it applies equally to all levels. Check if leadership also follows the same rules.',
  },
  {
    pattern: 'must\\s+(?:have|own)\\s+(?:own|your\\s+own)\\s+(?:equipment|computer|laptop|tools)',
    label: 'Must Provide Own Equipment',
    meaning:
      'Requiring employees to supply their own work equipment is a cost-cutting measure that may indicate broader financial problems or a lack of investment in employees.',
    advice:
      'Ask if there is a stipend or reimbursement for equipment. Factor the cost of equipment into your salary expectations. This is more common for 1099 contractor roles.',
  },
];

export const yellowFlags: FlagPattern[] = [
  {
    pattern: 'competitive\\s+(?:salary|compensation|pay)',
    label: 'Competitive Salary (No Range)',
    meaning:
      'When a company says "competitive" without providing a range, their definition of competitive may not match yours. It is often used to avoid committing to a number.',
    advice:
      'Ask for the salary range early in the interview process. Many states now require salary transparency in job postings, so check your local laws.',
  },
  {
    pattern: 'other\\s+duties\\s+as\\s+assigned',
    label: 'Other Duties as Assigned',
    meaning:
      'While common in job descriptions, this catch-all phrase means your role could expand beyond what was agreed upon. It gives the employer flexibility to add responsibilities without adjusting your title or pay.',
    advice:
      'Ask how often responsibilities shift and whether compensation is reviewed when duties expand significantly. Get your core responsibilities in writing.',
  },
  {
    pattern: 'self[- ]starter',
    label: 'Self-Starter',
    meaning:
      'This could mean you will receive limited direction, training, or support. While independence is valuable, a healthy workplace still provides guidance and resources.',
    advice:
      'Ask about management style, how frequently you will meet with your manager, and what resources and documentation are available for new hires.',
  },
  {
    pattern: 'entrepreneurial\\s+(?:mindset|spirit|environment)',
    label: 'Entrepreneurial Mindset',
    meaning:
      'This phrase often signals that you\'ll be expected to take ownership of problems beyond your role, potentially without the authority or resources to solve them properly.',
    advice:
      'Ask what budget and decision-making authority comes with the role. Clarify whether "entrepreneurial" means creative freedom or simply being under-resourced.',
  },
  {
    pattern: 'dynamic\\s+(?:environment|workplace|team)',
    label: 'Dynamic Environment',
    meaning:
      'Dynamic can mean exciting and varied, or it can mean chaotic and unpredictable. The difference is whether the change is intentional or a symptom of poor planning.',
    advice:
      'Ask for specific examples of how priorities are managed. Find out how often projects or goals change direction and what the decision-making process looks like.',
  },
  {
    pattern: 'flexible\\s+(?:schedule|hours?)\\s+required',
    label: 'Flexible Schedule Required',
    meaning:
      'When flexibility is required of you (rather than offered to you), it typically means irregular hours, weekend work, or being expected to adjust your schedule to the company\'s needs.',
    advice:
      'Ask what flexibility looks like specifically. Get clarity on core hours, weekend expectations, and whether the flexibility goes both ways.',
  },
  {
    pattern: 'some\\s+travel\\s+(?:required|expected|necessary)',
    label: 'Some Travel Required',
    meaning:
      'The word "some" is intentionally vague. It could mean once a quarter or twice a month. Without specifics, you cannot make an informed decision.',
    advice:
      'Ask for a specific travel percentage or number of trips per quarter. Find out the typical travel destinations, duration, and whether expenses are fully covered.',
  },
  {
    pattern: 'comfortable\\s+with\\s+ambiguity',
    label: 'Must Be Comfortable with Ambiguity',
    meaning:
      'This can mean the company is still figuring things out (acceptable in early-stage startups) or that processes are poorly defined and communication is lacking.',
    advice:
      'Ask about their product roadmap, planning cadence, and how decisions are communicated. Some ambiguity is normal; total ambiguity is a problem.',
  },
  {
    pattern: 'rapidly\\s+changing\\s+priorities',
    label: 'Rapidly Changing Priorities',
    meaning:
      'Frequent priority shifts suggest weak leadership, unclear strategy, or a reactive rather than proactive work culture. Your work may be discarded regularly.',
    advice:
      'Ask how priorities are set and how often they change. Find out what happens to in-progress work when priorities shift. Ask about the planning process.',
  },
  {
    pattern: 'lean\\s+team',
    label: 'Lean Team',
    meaning:
      'While lean can mean efficient, it often means understaffed. You may be doing the work of multiple people without the resources or headcount to support it.',
    advice:
      'Ask about team size, growth plans, and what support is available. Find out if the team is lean by design or because of budget constraints or attrition.',
  },
  {
    pattern: 'startup\\s+(?:mentality|culture|mindset|environment)',
    label: 'Startup Mentality',
    meaning:
      'At an actual startup, this is expected. At an established company, it usually means they want startup-level hustle without startup-level equity or upside.',
    advice:
      'If it is not actually a startup, ask why they use this framing. Determine if the equity, growth potential, or compensation reflect the expected commitment level.',
  },
  {
    pattern: 'we\\s+work\\s+hard',
    label: 'We Work Hard',
    meaning:
      'When a company feels the need to tell you they work hard, they are often setting expectations for long hours. Companies with healthy hours rarely need to state this.',
    advice:
      'Ask about average working hours, whether there are crunch periods, and how the company handles burnout prevention. Ask about after-hours communication norms.',
  },
  {
    pattern: 'open\\s+(?:office|floor\\s+plan|concept)',
    label: 'Open Office Plan',
    meaning:
      'Open offices are associated with more distractions, less privacy, increased noise, and higher stress. Research consistently shows they reduce productivity.',
    advice:
      'Ask if there are quiet rooms, phone booths, or options to work from home for focused work. Find out if noise-canceling headphones are the expected solution.',
  },
  {
    pattern: 'fast[- ]paced',
    label: 'Fast-Paced Environment',
    meaning:
      'On its own, this is a mild flag. Some fast-paced environments are exciting and well-managed. Others are simply chaotic and stressful.',
    advice:
      'Ask what "fast-paced" looks like day-to-day. Find out if there are structured sprints or if everything is always urgent. Ask about deadline-setting processes.',
  },
  {
    pattern: 'passionate',
    label: 'Must Be Passionate',
    meaning:
      'Requiring "passion" can be used to justify below-market pay or overwork. Your professionalism and competence should matter more than emotional investment.',
    advice:
      'Focus on demonstrating your skills and interest in the work without overcommitting emotionally. A job should value your output, not your feelings about it.',
  },
  {
    pattern: 'expected\\s+to\\s+(?:manage|handle)\\s+(?:own|your)\\s+(?:workload|time)',
    label: 'Manage Your Own Workload',
    meaning:
      'While autonomy is great, this phrasing can mean there is minimal management support and you will be blamed if things fall behind, even with unrealistic deadlines.',
    advice:
      'Ask how workload is distributed, what tools are used for project tracking, and how capacity is assessed when new work comes in.',
  },
];

export const greenFlags: FlagPattern[] = [
  {
    pattern: '\\$\\s*\\d[\\d,]*\\s*[-\u2013]\\s*\\$?\\s*\\d[\\d,]*',
    label: 'Specific Salary Range Listed',
    meaning:
      'Posting a clear salary range signals transparency, respect for candidates\' time, and a commitment to pay equity. It indicates a mature, well-organized hiring process.',
    advice:
      'This is a great sign. Research the range against market rates to confirm it is competitive. Target the upper half of the range in negotiations.',
  },
  {
    pattern: 'work[- ]life\\s+balance',
    label: 'Work-Life Balance',
    meaning:
      'Explicitly mentioning work-life balance suggests the company values sustainable working conditions and recognizes that burned-out employees are less productive.',
    advice:
      'Verify this in practice by asking about average working hours, after-hours email expectations, and whether leadership models work-life balance themselves.',
  },
  {
    pattern: 'professional\\s+development\\s+(?:budget|stipend|fund|allowance)',
    label: 'Professional Development Budget',
    meaning:
      'A dedicated learning budget shows the company invests in employee growth. This benefits both your career trajectory and your day-to-day job satisfaction.',
    advice:
      'Ask about the specific amount, what it covers (conferences, courses, books, certifications), and how the budget is accessed and approved.',
  },
  {
    pattern: '401\\s*\\(?k\\)?\\s*match',
    label: '401(k) Match',
    meaning:
      'Employer matching on retirement contributions is a significant financial benefit and indicates the company has a mature, well-structured benefits program.',
    advice:
      'Ask about the match percentage, vesting schedule, and any waiting periods. A 4-6% match with immediate vesting is excellent.',
  },
  {
    pattern: '(?:fully\\s+)?remote|hybrid|work\\s+from\\s+(?:home|anywhere)',
    label: 'Remote or Hybrid Work',
    meaning:
      'Offering location flexibility shows trust in employees and a focus on results over physical presence. It also signals a more modern, adaptive work culture.',
    advice:
      'Clarify the specifics: is remote truly remote or limited to certain time zones? For hybrid, how many days in-office? Ask if the policy has changed recently.',
  },
  {
    pattern: 'mentorship\\s+(?:program|opportunities|pairing)',
    label: 'Mentorship Program',
    meaning:
      'Structured mentorship shows the company is invested in long-term employee development, knowledge sharing, and building a supportive culture.',
    advice:
      'Ask how the mentorship program works in practice, how mentors are matched, and what outcomes previous mentees have experienced.',
  },
  {
    pattern: 'regular\\s+(?:1[- ]on[- ]1|one[- ]on[- ]one|check[- ]in)s?',
    label: 'Regular 1-on-1 Meetings',
    meaning:
      'Committing to regular check-ins indicates a management culture focused on feedback, growth, and open communication rather than top-down directives.',
    advice:
      'Ask about the cadence (weekly is ideal) and whether these meetings focus on career development or just status updates.',
  },
  {
    pattern: 'divers(?:e|ity)\\s+(?:team|workforce|and\\s+inclusion|equity)',
    label: 'Diversity and Inclusion Commitment',
    meaning:
      'Actively highlighting diversity signals the company values different perspectives and is working toward an inclusive environment.',
    advice:
      'Look for specifics: employee resource groups, diversity metrics, inclusive hiring practices. Actions speak louder than a single line in a job description.',
  },
  {
    pattern: '(?:clear\\s+)?(?:promotion|career|growth)\\s+(?:path|ladder|framework|track)',
    label: 'Clear Promotion Path',
    meaning:
      'Defined career progression shows the company has invested in employee retention and has transparent expectations for advancement.',
    advice:
      'Ask to see the career ladder or leveling framework. Ask about average time between promotions and what the criteria are.',
  },
  {
    pattern: 'parental\\s+leave',
    label: 'Parental Leave',
    meaning:
      'Offering parental leave beyond the legal minimum indicates the company supports employees through major life events and values long-term retention.',
    advice:
      'Ask about the duration for both primary and secondary caregivers, whether it is fully paid, and what the return-to-work transition looks like.',
  },
  {
    pattern: 'health\\s+(?:insurance|benefits|coverage).*(?:dental|vision)|(?:dental|vision).*health\\s+(?:insurance|benefits|coverage)',
    label: 'Comprehensive Health Benefits',
    meaning:
      'Listing specific health benefits (medical, dental, vision) signals a genuine investment in employee wellbeing, not just a checkbox.',
    advice:
      'Ask about premium costs, deductibles, and whether the plan covers dependents. Find out when coverage begins relative to your start date.',
  },
  {
    pattern: 'paid\\s+(?:sick\\s+(?:leave|days|time)|time\\s+off)',
    label: 'Paid Time Off / Sick Leave',
    meaning:
      'Clearly stated paid time off shows the company respects employee health and personal time. Specificity here is a good sign.',
    advice:
      'Ask about the specific number of days, accrual policy, rollover rules, and whether PTO and sick leave are tracked separately.',
  },
];
